import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Headphones, BookOpen, ScrollText, ArrowRight, Star, Sparkles, Mic, Video, Crown, Award, Share2, UserPlus, LogIn, ChevronDown, Users } from "lucide-react";
import axios from "axios";
import { Tx } from "../context/AutoTranslateContext";
import TranslationNotice from "../components/TranslationNotice";
import { SURAHS } from "../data/surahs";
import { TEACHERS, LISTENING_ONLY } from "../data/reciters";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_KEY = "icc_academy_role";

const RoleEntry = ({ testid, role, icon: Icon, label, registerTo, signInTo, active, onChoose }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const choose = () => { onChoose(role); setOpen(false); };
  return (
    <div className="relative" data-testid={testid} ref={ref}>
      <button
        type="button"
        data-testid={`${testid}-toggle`}
        onClick={() => setOpen((v) => !v)}
        className={`w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-forest text-sand hover:bg-sage rounded-full px-7 py-3.5 text-sm font-semibold transition-all hover:-translate-y-0.5 shadow-lg shadow-forest/20 ${active ? "ring-2 ring-gold ring-offset-2 ring-offset-cream" : ""}`}
      >
        <Icon className="w-4.5 h-4.5" />
        <Tx>{label}</Tx>
        {active && <span className="text-[10px] bg-gold/20 text-gold px-2 py-0.5 rounded-full"><Tx>last used</Tx></span>}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid={`${testid}-menu`}
          className="absolute left-1/2 -translate-x-1/2 mt-2 w-60 z-30 bg-white rounded-2xl border border-gold/25 shadow-xl p-2"
        >
          <Link
            data-testid={`${testid}-register`}
            to={registerTo}
            onClick={choose}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-forest hover:bg-cream transition"
          >
            <UserPlus className="w-4 h-4 text-gold" />
            <span><Tx>Register</Tx> <span className="text-mute text-xs">(<Tx>new account</Tx>)</span></span>
          </Link>
          <Link
            data-testid={`${testid}-signin`}
            to={signInTo}
            onClick={choose}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-forest hover:bg-cream transition"
          >
            <LogIn className="w-4 h-4 text-gold" />
            <span><Tx>Sign In</Tx> <span className="text-mute text-xs">(<Tx>existing account</Tx>)</span></span>
          </Link>
        </div>
      )}
    </div>
  );
};

const featuredSurahs = [
  SURAHS[0],   // Fatiha
  SURAHS[35],  // Ya-Sin
  SURAHS[55],  // Ar-Rahman
  SURAHS[66],  // Al-Mulk
  SURAHS[17],  // Al-Kahf
  SURAHS[111], // Ikhlas
  SURAHS[112], // Al-Falaq
  SURAHS[113], // An-Nas
];

const comingSoon = [
  { icon: Video, title: "Group classes & recorded lessons", body: "Scheduled group tajweed halaqas plus a growing library of recorded classes." },
  { icon: Sparkles, title: "Monthly marketing posters", body: "Automated tajweed & Sunnah posters posted to your feed every month." },
];

const Academy = () => {
  const [poster, setPoster] = useState(null);
  const [activeRole, setActiveRole] = useState(null);
  useEffect(() => { try { setActiveRole(localStorage.getItem(ROLE_KEY)); } catch { /* ignore */ } }, []);
  const chooseRole = (r) => { try { localStorage.setItem(ROLE_KEY, r); } catch { /* ignore */ } setActiveRole(r); };
  useEffect(() => {
    axios.get(`${API}/academy/poster/current`).then((r) => {
      // Rebuild share URLs using the actual browser origin (backend may see an
      // internal Kubernetes URL). Everything else — content, verse, hashtags —
      // stays as-is.
      const p = r.data;
      const origin = window.location.origin;
      const link = origin + p.path;
      const raw = `*${p.title}*\n${p.subtitle}\n\n${p.cta}\n\n${link}\n\n${p.hashtags}`;
      const enc = encodeURIComponent;
      p.link = link;
      p.share = {
        whatsapp: `https://wa.me/?text=${enc(raw)}`,
        twitter:  `https://twitter.com/intent/tweet?text=${enc(p.title + " — " + p.cta)}&url=${enc(link)}`,
        telegram: `https://t.me/share/url?url=${enc(link)}&text=${enc(p.title)}`,
        raw_text: raw,
      };
      setPoster(p);
    }).catch(() => {});
  }, []);
  const copyCaption = () => {
    try {
      navigator.clipboard.writeText(poster?.share?.raw_text || "");
      alert("Caption copied — paste into Instagram / TikTok / Facebook.");
    } catch (e) { console.debug("clipboard write failed:", e); }
  };

  return (
    <div data-testid="academy-page" className="max-w-6xl mx-auto px-4 sm:px-8 py-16">
    {/* Hero */}
    <div className="text-center mb-14">
      <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3"><Tx>The Academy</Tx></p>
      <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-forest font-bold mb-5">
        <Tx>Qur'an & Arabic Academy</Tx>
      </h1>
      <p className="text-mute leading-relaxed text-lg max-w-2xl mx-auto">
        <Tx>Learn the Qur'an from the classical masters, listen to twenty world-renowned qurrā', and study Arabic from the alphabet to grammar.</Tx>
      </p>
      <div className="mt-5">
        <Link to="/academy/how-it-works" data-testid="academy-how-it-works-link"
          className="inline-flex items-center gap-2 text-sm text-forest font-medium hover:underline">
          <Tx>How it works — step by step</Tx> <ArrowRight className="w-3.5 h-3.5 rtl:rotate-180" />
        </Link>
        <Link to="/academy/my-sessions" data-testid="academy-my-sessions-link"
          className="inline-flex items-center gap-2 text-sm text-forest font-medium hover:underline ml-5">
          <Tx>My sessions</Tx> <ArrowRight className="w-3.5 h-3.5 rtl:rotate-180" />
        </Link>
      </div>

      {/* Role entry — Student / Tutor → Register or Sign In */}
      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4" data-testid="academy-role-entry">
        <RoleEntry
          testid="role-student"
          role="student"
          icon={GraduationCap}
          label="I am a Student"
          registerTo="/register"
          signInTo="/login"
          active={activeRole === "student"}
          onChoose={chooseRole}
        />
        <RoleEntry
          testid="role-tutor"
          role="tutor"
          icon={Users}
          label="I am a Tutor"
          registerTo="/academy/tutors/apply"
          signInTo="/academy/my-sessions"
          active={activeRole === "tutor"}
          onChoose={chooseRole}
        />
      </div>
    </div>

    {/* Live AI translation reassurance */}
    <TranslationNotice className="mb-10" />

    {/* THE FOUR TRACKS — kept strictly separate */}
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
      {/* Track 1 — LEARNING: with the specific classical masters */}
      <Link
        data-testid="track-learn"
        to="/academy/teachers"
        className="clay-surface rounded-3xl p-6 group hover:bg-white transition border-2 border-gold/30"
      >
        <div className="w-12 h-12 rounded-full bg-gold/15 text-gold flex items-center justify-center mb-4">
          <GraduationCap className="w-6 h-6" />
        </div>
        <div className="flex items-center gap-2 mb-2">
          <h2 className="font-heading text-lg text-forest font-bold"><Tx>Learn Tajweed</Tx></h2>
          <Star className="w-4 h-4 text-gold" />
        </div>
        <p className="text-xs text-mute leading-relaxed mb-4">
          <Tx>Recite alongside Sheikh Al-Husary, Al-Minshawi, Muhammad Jibril, Abdul Basit and Muhammad Ayyub — the classical masters of ahkām al-tilāwa.</Tx>
        </p>
        <span className="text-xs text-forest font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
          <Tx>Meet the teachers</Tx> <ArrowRight className="w-3 h-3 rtl:rotate-180" />
        </span>
      </Link>

      {/* Track 2 — LISTENING: the 20 reciters library */}
      <Link
        data-testid="track-listen"
        to="/academy/quran/1"
        className="clay-surface rounded-3xl p-6 group hover:bg-white transition"
      >
        <div className="w-12 h-12 rounded-full bg-forest/10 text-forest flex items-center justify-center mb-4">
          <Headphones className="w-6 h-6" />
        </div>
        <h2 className="font-heading text-lg text-forest font-bold mb-2"><Tx>Listening Library</Tx></h2>
        <p className="text-xs text-mute leading-relaxed mb-4">
          <Tx>Twenty world-renowned reciters — with Tafsīr Al-Muyassar and English translation for every ayah.</Tx>
        </p>
        <span className="text-xs text-forest font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
          <Tx>Open the reader</Tx> <ArrowRight className="w-3 h-3 rtl:rotate-180" />
        </span>
      </Link>

      {/* Track 3 — LIVE TUTORS */}
      <Link
        data-testid="track-tutors"
        to="/academy/tutors"
        className="clay-surface rounded-3xl p-6 group hover:bg-white transition"
      >
        <div className="w-12 h-12 rounded-full bg-forest/10 text-forest flex items-center justify-center mb-4">
          <Video className="w-6 h-6" />
        </div>
        <h2 className="font-heading text-lg text-forest font-bold mb-2"><Tx>Live 1-to-1 Tutors</Tx></h2>
        <p className="text-xs text-mute leading-relaxed mb-3">
          <Tx>Book qualified male & female tutors for tajweed, ḥifẓ, Ijāza and Arabic — join via private video call.</Tx>
        </p>
        <p className="text-[11px] text-gold font-medium mb-4">
          <Tx>£8 for one 30-min lesson · £25 for four lessons a month · £40 for eight lessons a month</Tx>
        </p>
        <span className="text-xs text-forest font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
          <Tx>See plans & subscribe</Tx> <ArrowRight className="w-3 h-3 rtl:rotate-180" />
        </span>
      </Link>

      {/* Track 4 — ARABIC LANGUAGE */}
      <Link
        data-testid="track-arabic"
        to="/academy/arabic"
        className="clay-surface rounded-3xl p-6 group hover:bg-white transition"
      >
        <div className="w-12 h-12 rounded-full bg-forest/10 text-forest flex items-center justify-center mb-4">
          <ScrollText className="w-6 h-6" />
        </div>
        <h2 className="font-heading text-lg text-forest font-bold mb-2"><Tx>Learn Arabic</Tx></h2>
        <p className="text-xs text-mute leading-relaxed mb-4">
          <Tx>The 28 letters, top Qur'an words, grammar essentials, sun/moon letters, verb conjugation — from zero.</Tx>
        </p>
        <span className="text-xs text-forest font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
          <Tx>Start learning</Tx> <ArrowRight className="w-3 h-3 rtl:rotate-180" />
        </span>
      </Link>
    </div>

    {/* Pronunciation Coach callout */}
    <Link
      data-testid="cta-pronunciation"
      to="/academy/pronunciation"
      className="clay-surface rounded-3xl p-6 mb-6 grid sm:grid-cols-[auto_1fr_auto] gap-4 items-center hover:bg-white transition"
    >
      <div className="w-12 h-12 rounded-full bg-gold/15 text-gold flex items-center justify-center">
        <Mic className="w-6 h-6" />
      </div>
      <div>
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-gold"><Tx>New</Tx></span>
          <h3 className="font-heading text-lg text-forest font-bold"><Tx>AI Pronunciation Coach</Tx></h3>
        </div>
        <p className="text-xs text-mute leading-relaxed">
          <Tx>Record yourself reciting any ayah — Whisper transcribes and we highlight words to work on.</Tx>
        </p>
      </div>
      <span className="text-xs text-forest font-medium inline-flex items-center gap-1">
        <Tx>Try it</Tx> <ArrowRight className="w-3 h-3 rtl:rotate-180" />
      </span>
    </Link>

    {/* Academy Pass + Certificates row */}
    <div className="grid md:grid-cols-2 gap-5 mb-14">
      <Link
        data-testid="cta-academy-pass"
        to="/academy/pass"
        className="rounded-3xl p-6 bg-forest text-sand hover:bg-sage transition grid grid-cols-[auto_1fr_auto] gap-4 items-center"
      >
        <div className="w-12 h-12 rounded-full bg-gold/25 text-gold flex items-center justify-center">
          <Crown className="w-6 h-6" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-gold mb-1"><Tx>Academy Pass</Tx></p>
          <h3 className="font-heading text-lg font-bold mb-1"><Tx>£12 for 30 days of full access</Tx></h3>
          <p className="text-xs text-sand/80 leading-relaxed">
            <Tx>One payment of £12 unlocks the Coloured Mushaf, 20+ reciters, Tafsir, Arabic curriculum and AI coach for 30 days. No auto-renewal.</Tx>
          </p>
        </div>
        <ArrowRight className="w-5 h-5 rtl:rotate-180" />
      </Link>

      <Link
        data-testid="cta-certificates"
        to="/academy/certificates"
        className="clay-surface rounded-3xl p-6 hover:bg-white transition grid grid-cols-[auto_1fr_auto] gap-4 items-center border-2 border-gold/40"
      >
        <div className="w-12 h-12 rounded-full bg-gold/15 text-gold flex items-center justify-center">
          <Award className="w-6 h-6" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-gold mb-1"><Tx>Certificates</Tx></p>
          <h3 className="font-heading text-lg text-forest font-bold mb-1"><Tx>£8 per Juz' · £80 for the full Ijāza</Tx></h3>
          <p className="text-xs text-mute leading-relaxed">
            <Tx>Both are one-time payments. Each certificate is issued only after a live 30-min review with an approved qārī — never automatic.</Tx>
          </p>
        </div>
        <ArrowRight className="w-5 h-5 text-forest rtl:rotate-180" />
      </Link>
    </div>

    {/* Popular surahs quick-jump */}
    <section className="mb-14">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading text-2xl text-forest font-semibold"><Tx>Popular surahs — start here</Tx></h2>
        <Link to="/academy/quran/1" className="text-xs text-mute hover:text-forest inline-flex items-center gap-1">
          <Tx>Browse all 114</Tx> <ArrowRight className="w-3 h-3 rtl:rotate-180" />
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {featuredSurahs.map((s) => (
          <Link
            key={s.n}
            data-testid={`featured-surah-${s.n}`}
            to={`/academy/quran/${s.n}`}
            className="clay-surface rounded-2xl p-4 hover:bg-cream transition"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="w-7 h-7 rounded-full bg-forest/10 text-forest text-[10px] font-bold flex items-center justify-center">{s.n}</span>
              <span className="arabic text-gold text-lg">{s.arabic}</span>
            </div>
            <p className="text-sm font-medium text-forest">{s.name}</p>
            <p className="text-[10px] text-mute mt-1">{s.ayahs} <Tx>ayat</Tx></p>
          </Link>
        ))}
      </div>
    </section>

    {/* Coming soon */}
    <section>
      <h2 className="font-heading text-2xl text-forest font-semibold mb-5"><Tx>Coming soon</Tx></h2>
      <div className="grid sm:grid-cols-3 gap-4">
        {comingSoon.map((h, i) => {
          const Icon = h.icon;
          return (
            <div key={i} data-testid={`soon-${i}`} className="bg-white rounded-2xl border border-gold/25 p-5 opacity-90">
              <div className="w-11 h-11 rounded-full bg-gold/15 text-gold flex items-center justify-center mb-3">
                <Icon className="w-5 h-5" />
              </div>
              <p className="text-sm text-ink font-semibold mb-1"><Tx>{h.title}</Tx></p>
              <p className="text-xs text-mute leading-relaxed"><Tx>{h.body}</Tx></p>
            </div>
          );
        })}
      </div>
    </section>

    {/* Monthly share poster — the "Direct Share" feature from the spec */}
    {poster && (
      <section data-testid="academy-share-poster" className="mt-14 rounded-3xl bg-forest text-sand p-8">
        <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-gold mb-2 inline-flex items-center gap-1">
              <Share2 className="w-3 h-3" /> <Tx>Share this month's poster</Tx>
            </p>
            <h2 className="font-heading text-2xl font-bold mb-2">{poster.title}</h2>
            <p className="text-sm text-sand/80 mb-3">{poster.subtitle}</p>
            <p className="arabic text-lg text-gold mb-1" dir="rtl">{poster.verse.arabic}</p>
            <p className="text-[11px] italic text-sand/70 mb-4">{poster.verse.en}</p>
            <p className="text-xs text-gold">{poster.cta}</p>
          </div>
          <div className="flex flex-wrap gap-2 md:flex-col md:items-end">
            <a data-testid="share-whatsapp" href={poster.share.whatsapp} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-4 py-2 text-xs font-medium hover:opacity-90">
              WhatsApp
            </a>
            <a data-testid="share-twitter" href={poster.share.twitter} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#1DA1F2] text-white rounded-full px-4 py-2 text-xs font-medium hover:opacity-90">
              X / Twitter
            </a>
            <a data-testid="share-telegram" href={poster.share.telegram} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#26A5E4] text-white rounded-full px-4 py-2 text-xs font-medium hover:opacity-90">
              Telegram
            </a>
            <button data-testid="share-copy-caption" onClick={copyCaption}
              className="inline-flex items-center gap-2 bg-gold/25 text-gold rounded-full px-4 py-2 text-xs font-medium hover:bg-gold/40">
              <Tx>Copy Instagram caption</Tx>
            </button>
          </div>
        </div>
      </section>
    )}

    <div className="mt-14 text-center bg-cream/60 rounded-3xl p-8 border border-gold/20">
      <Sparkles className="w-5 h-5 text-gold mx-auto mb-2" />
      <p className="text-sm text-mute italic max-w-xl mx-auto">
        <Tx>Your reciter choice, bookmarks and repeat count are saved on this device. Sign in to sync them across your phone, tablet and computer.</Tx>
      </p>
    </div>
  </div>
  );
};

export default Academy;
